// src/pages/inventory/commerce/shopify/tabs/OrderSyncTab.tsx
import React, { useCallback } from "react";
import { Button, Space, Typography, Alert, Row, Col, DatePicker, Form, Select, Divider, Statistic } from "antd";
import { ImportOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { importOrdersFromShopify } from "../../../../api/inventory/shopifyApi";
import type { ShopifyImportResults } from "../../../../types/inventory/ShopifyTypes";
import { useShopifyImportJob } from "../../../../hooks/useShopifyImportJob";
import { useNotifications } from "../../../../context/NotificationContext";
import dayjs from "dayjs";
import { SectionCard } from "../../../../components/shared";

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const OrderSyncTab: React.FC = () => {
	const [form] = Form.useForm();
	const { showError, showSuccess } = useNotifications();

	const startImport = useCallback(() => {
		const values = form.getFieldsValue();

		const params: any = {
			status: values.status || "any",
		};

		if (values.dateRange) {
			params.created_at_min = values.dateRange[0].toISOString();
			params.created_at_max = values.dateRange[1].toISOString();
		}

		return importOrdersFromShopify(params);
	}, [form]);

	const handleCompleted = useCallback(
		(results: ShopifyImportResults | null) => {
			// CU-86d42ap9h — `created` is canonical; `imported` is only a backend alias.
			showSuccess(`Order import completed. Created: ${results?.created ?? 0}, Updated: ${results?.updated ?? 0}`);
		},
		[showSuccess]
	);

	const handleFailed = useCallback(
		(_results: ShopifyImportResults | null, message: string, detail?: string) => {
			showError(message, detail);
		},
		[showError]
	);

	const {
		phase,
		running: syncing,
		busy,
		trackingPaused,
		results: lastSyncResult,
		message: failureMessage,
		run: handleImportOrders,
		resume: handleResumeTracking,
	} = useShopifyImportJob({
		label: "Order",
		start: startImport,
		onCompleted: handleCompleted,
		onFailed: handleFailed,
	});

	return (
		<div>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				<Alert message="Import Shopify Orders" description="Import orders from your Shopify store into your commerce system. This will create sales orders from Shopify transactions." type="info" showIcon icon={<ShoppingCartOutlined />} />

				<SectionCard title="Import Configuration">
					<Form form={form} layout="vertical">
						<Row gutter={16}>
							<Col xs={24} md={12}>
								<Form.Item label="Date Range" name="dateRange" help="Leave empty to import all orders">
									<RangePicker style={{ width: "100%" }} />
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Form.Item label="Order Status" name="status" initialValue="any">
									<Select>
										<Select.Option value="any">Any Status</Select.Option>
										<Select.Option value="open">Open</Select.Option>
										<Select.Option value="closed">Closed</Select.Option>
										<Select.Option value="cancelled">Cancelled</Select.Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>
					</Form>

					<Divider />

					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<Text type="secondary">Orders will be imported as sales orders in your commerce system</Text>
						<Button type="primary" icon={<ImportOutlined />} onClick={handleImportOrders} loading={syncing} disabled={busy}>
							{busy ? (phase === "queued" ? "Queued…" : "Importing…") : "Import Orders"}
						</Button>
					</div>
				</SectionCard>

				{syncing && <Alert message={phase === "queued" ? "Import queued" : "Import in progress"} description="This runs in the background. Results appear here once it finishes." type="info" showIcon />}

				{trackingPaused && (
					<Alert
						message="Status tracking paused"
						description={failureMessage ?? "Unable to check import status right now. The import may still be running."}
						type="warning"
						showIcon
						action={
							<Button size="small" onClick={handleResumeTracking}>
								Retry Status
							</Button>
						}
					/>
				)}

				{phase === "failed" && <Alert message={failureMessage ?? "Order import failed."} description={lastSyncResult ? "The counts below are the partial progress made before the import stopped. The import did not finish." : undefined} type="error" showIcon />}

				{lastSyncResult && (
					<SectionCard title={phase === "failed" ? "Partial Import Progress" : "Last Import Results"}>
						<Row gutter={16}>
							<Col xs={24} sm={8}>
								<Statistic title="Created" value={lastSyncResult.created} valueStyle={{ color: "var(--mg-success)" }} />
							</Col>
							<Col xs={24} sm={8}>
								<Statistic title="Updated" value={lastSyncResult.updated} valueStyle={{ color: "var(--mg-primary)" }} />
							</Col>
							<Col xs={24} sm={8}>
								<Statistic title="Failed" value={lastSyncResult.failed} valueStyle={{ color: "var(--mg-danger)" }} />
							</Col>
						</Row>
					</SectionCard>
				)}

				<SectionCard title="How Order Sync Works">
					<Paragraph>
						<ul>
							<li>Orders are imported from Shopify with all customer and item information</li>
							<li>Product associations are maintained through Shopify product IDs</li>
							<li>Order status, payment status, and fulfillment status are tracked</li>
							<li>Customer information including shipping and billing addresses is preserved</li>
							<li>Tax and shipping amounts are recorded separately</li>
						</ul>
					</Paragraph>
				</SectionCard>
			</Space>
		</div>
	);
};

export default OrderSyncTab;
